import * as PIXI from "pixi.js";

interface SpriteStyleOptions {
  width: number;
  height: number;
  borderRadius?: string;
}

/**
 * Get CSS style for rendering a texture from a spritesheet as a background image.
 * Returns null if texture is not available.
 */
export function getTextureStyle(
  tex: PIXI.Texture | undefined,
  options: SpriteStyleOptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  if (!tex) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { source, frame } = tex as any;
  const baseUrl =
    source?.url ??
    source?.label ??
    source?.options?.src ??
    source?._sourceOrigin ??
    source?.resource?.src ??
    source?._resource?.src;

  const pixelW = source?.pixelWidth ?? source?.width;
  const pixelH = source?.pixelHeight ?? source?.height;
  if (!baseUrl || !frame || !pixelW || !pixelH) return null;

  const res =
    source?.resolution ?? (pixelW && source?.width ? pixelW / source.width : 1);
  const s = res;

  const fx = (frame.x ?? 0) * s;
  const fy = (frame.y ?? 0) * s;
  const fw = (frame.width ?? options.width) * s;
  const fh = (frame.height ?? options.height) * s;

  const ratioX = fw ? options.width / fw : 1;
  const ratioY = fh ? options.height / fh : ratioX;
  const bgW = pixelW * ratioX;
  const bgH = pixelH * ratioY;

  return {
    width: options.width,
    height: options.height,
    borderRadius: options.borderRadius,
    backgroundImage: `url(${baseUrl})`,
    backgroundPosition: `-${fx * ratioX}px -${fy * ratioY}px`,
    backgroundSize: `${bgW}px ${bgH}px`,
    backgroundRepeat: "no-repeat",
  };
}

/**
 * Get a texture from the avatars spritesheet by frame name.
 * Returns null if spritesheet or texture is not available.
 */
export function getAvatarTexture(frameName: string): PIXI.Texture | null {
  if (!PIXI.Assets.cache?.has?.("avatars")) return null;

  const sheet = PIXI.Assets.cache.get("avatars");
  return sheet?.textures?.[frameName] ?? null;
}

/**
 * Get CSS style for an avatar from the spritesheet.
 * Returns null if texture is not available.
 */
export function getAvatarStyle(
  frameName: string,
  size: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const tex = getAvatarTexture(frameName);
  if (!tex) return null;

  return getTextureStyle(tex, {
    width: size,
    height: size,
    borderRadius: "50%",
  });
}
